import { addParamsToURL, extendPathname, proxyClientOptions, RestfulClient, Session } from '@benzinga/session';
import { SafePromise, SafeResilientParams } from '@benzinga/safe-await';
import { CalendarEnvironment } from '../../environment';
import {
  AnalystData,
  CalendarDataParams,
  CalendarType,
  CalendarTypeEvents,
  CompanyEventDetails,
  CompanyEventsQueryParams,
  RatingsAnalystsParams,
  SEC,
  SecCalendarParams,
} from '../../entities';
import { toProxyQueryParams, toUpstreamWireParams } from '../../utils';
import { calendarDataRoutes } from './routes';
import { CalendarDataTransport } from './types';

const CALENDAR_TYPE_TO_SLUG: Partial<Record<CalendarType, string>> = {
  optionsActivity: 'options-activity',
};

export class CalendarDataRestful extends RestfulClient implements CalendarDataTransport {
  commonResilience: SafeResilientParams<unknown, unknown> = {
    delayOffset: 30000,
    delaySquare: 1000,
    maxNumOfAttempts: 10,
    retryOnError: true,
    returnLastError: true,
  };

  constructor(session: Session) {
    const env = session.getEnvironment(CalendarEnvironment);
    super(env.dataUrl, session, {}, proxyClientOptions(undefined, 'token'));
  }

  public isUsingProxy = (): boolean => this.usingProxy;

  fetchCalendarData = <T extends CalendarType>(
    calendarType: CalendarType,
    params: CalendarDataParams,
  ): SafePromise<CalendarTypeEvents[T]> => {
    if (this.usingProxy) {
      const slug = CALENDAR_TYPE_TO_SLUG[calendarType] ?? calendarType;
      const path = `${this.routePath(calendarDataRoutes.calendarData)}/${slug}`;
      const url = this.URL(path, toProxyQueryParams(params));
      return this.get(url, { allowsAnonymousAuth: true, credentials: 'omit' });
    }

    const wireParams = toUpstreamWireParams(params);
    const possibleTokenOverride = this.session.getEnvironment(CalendarEnvironment).token;
    if (possibleTokenOverride) {
      wireParams.token = possibleTokenOverride;
    }
    const url = this.URL(`${calendarDataRoutes.calendarData.upstreamPath}/${calendarType}`, wireParams);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'omit',
      resilience: this.commonResilience,
    });
  };

  getRatingsAnalysts = async (
    query: RatingsAnalystsParams,
  ): SafePromise<{ analyst_ratings_analyst: AnalystData[] }> => {
    const env = this.session.getEnvironment(CalendarEnvironment);
    const path = this.routePath(calendarDataRoutes.ratingsAnalysts);
    const url = this.URL(
      path,
      this.withUpstreamCredentials(
        {
          analyst: query.analyst,
          fields: query.fields,
          page: query.page,
          pagesize: query.pageSize,
          updated: query.updated,
        },
        { token: env.token },
      ),
    );
    return this.get(url, {
      allowsAnonymousAuth: true,
      ...(this.usingProxy ? { credentials: 'omit' } : { resilience: this.commonResilience }),
    });
  };

  getCompanyEvents = async (query: CompanyEventsQueryParams): SafePromise<{ events: CompanyEventDetails[] }> => {
    const env = this.session.getEnvironment(CalendarEnvironment);
    const path = this.routePath(calendarDataRoutes.companyEvents);

    if (this.usingProxy) {
      const url = this.URL(path, {
        ...(query.page != null && { page: query.page }),
        ...(query.pageSize != null && { pageSize: query.pageSize }),
        ...(query.date_from && { dateFrom: query.date_from }),
        ...(query.date_to && { dateTo: query.date_to }),
        ...(query.tickers && { symbols: query.tickers }),
        ...(query.updated != null && { updated: query.updated }),
        ...(query.importance != null && { importance: query.importance }),
      });
      return this.get(url, { allowsAnonymousAuth: true, credentials: 'omit' });
    }

    const url = this.URL(
      path,
      this.withUpstreamCredentials(
        {
          page: query.page,
          pagesize: query.pageSize,
          'parameters[date_from]': query.date_from,
          'parameters[date_to]': query.date_to,
          'parameters[tickers]': query.tickers,
          updated: query.updated,
          ...(query.importance != null ? { 'parameters[importance]': query.importance } : {}),
        },
        { token: env.eventsToken },
      ),
    );
    return this.get(url, {
      allowsAnonymousAuth: true,
      resilience: this.commonResilience,
    });
  };

  getSecCalendarData = async (query: SecCalendarParams): SafePromise<{ sec: SEC[] }> => {
    if (this.usingProxy) {
      const symbols = Array.isArray(query.symbols) ? query.symbols.join(',') : query.symbols;
      const dateFrom = query.dateFrom instanceof Date ? query.dateFrom.toISOString().slice(0, 10) : query.dateFrom;
      const dateTo = query.dateTo instanceof Date ? query.dateTo.toISOString().slice(0, 10) : query.dateTo;
      const url = this.URL(this.routePath(calendarDataRoutes.secCalendarData), {
        ...query,
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(symbols && { symbols }),
      });
      return this.get(url, { allowsAnonymousAuth: true, credentials: 'omit' });
    }

    const calendarEnv = this.session.getEnvironment(CalendarEnvironment);
    const secBase = calendarEnv.secDataUrl ?? calendarEnv.dataUrl;
    const actualParams: Record<string, unknown> = {
      ...toUpstreamWireParams(query),
      ...(query.display && { display: query.display }),
      token: calendarEnv.secToken,
    };
    if (this.session.getAuthenticationManager().isUsingApiKey()) {
      const tokenParameterName = 'token';
      const possibleUserProvidedValue = actualParams[tokenParameterName];
      actualParams[tokenParameterName] =
        possibleUserProvidedValue || this.session.getAuthenticationManager().getApiKey();
    }
    const url = addParamsToURL(
      extendPathname(secBase, calendarDataRoutes.secCalendarData.upstream),
      actualParams as object,
    );
    return this.get(url, { allowsAnonymousAuth: true, resilience: this.commonResilience });
  };
}
